import React, { useEffect, useMemo, useRef, useState } from "react";
import { Sparkle, X, PaperPlaneRight, ChatCircle, Robot, HandsPraying, Heart, SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAudio } from "../context/AudioContext";
import { LANGUAGES } from "../lib/constants";
import { toast } from "sonner";
import useDraggableFAB from "../hooks/useDraggableFAB";
import DictationButton from "./DictationButton";

const NARRATE_STORAGE_KEY = "hw:ai:narrate";

export default function AskAI() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // [{role, content, kind?, passage?, saved?}]
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [narrate, setNarrate] = useState(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem(NARRATE_STORAGE_KEY);
    return stored === null ? true : stored === "1";
  });
  const endRef = useRef(null);
  const { language, play, stop: stopAudio, voiceURI, voices } = useAudio();
  // Make the floating "Ask AI" bubble drag-and-droppable. Long-press
  // and drag to reposition; short taps still open the AI panel.
  const fab = useDraggableFAB({
    storageKey: "hw:fab:askai",
    defaultPosition: { right: 32, bottom: 176 }, // matches old bottom-44 / right-8
    size: 56,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(NARRATE_STORAGE_KEY, narrate ? "1" : "0");
  }, [narrate]);

  const speakAnswer = useMemo(() => (answer, langOverride) => {
    const langCfg = LANGUAGES.find((l) => l.code === (langOverride || language)) || LANGUAGES[0];
    const cleaned = String(answer || "")
      .replace(/\*\*([^*]+)\*\*/g, "$1")
      .replace(/\*([^*]+)\*/g, "$1")
      .replace(/^#+\s*/gm, "")
      .trim();
    const chunks = cleaned
      .split(/(?<=[.!?…])\s+(?=[A-ZÀ-ÖØ-Þ"“‘])|\n\n+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!chunks.length) return;
    const queue = chunks.map((text, i) => ({ id: `ai-${Date.now()}-${i}`, text }));
    const voiceMatch = (voices || []).find((v) => v.voiceURI === voiceURI && (v.lang || "").toLowerCase().startsWith(langCfg.voice.slice(0, 2).toLowerCase()));
    const fallbackVoice = (voices || []).find((v) => (v.lang || "").toLowerCase().startsWith(langCfg.voice.slice(0, 2).toLowerCase()));
    const targetVoiceLocale = (voiceMatch || fallbackVoice)?.lang || langCfg.voice;
    play(queue, { kind: "ai", title: "Companion" }, langCfg.code, targetVoiceLocale, { translate: false });
  }, [language, play, voices, voiceURI]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: "ai",
        content:
          "Peace to you. I'm Holy Words Companion. Ask me anything about the Bible — a verse, a story, a doctrine, a hard question of life. I'll do my best to ground every answer in Scripture.\n\nNeed a quiet moment? Tap \"Pray with me\" below and I'll lead you in a one-minute guided prayer in your language.",
      }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, busy]);

  const send = async (e, overrideText, displayText, onComplete, meta) => {
    e?.preventDefault?.();
    const text = (overrideText ?? input).trim();
    if (!text || busy) return;
    if (!overrideText) setInput("");
    setMessages((m) => [...m, { role: "user", content: displayText || text }]);
    setBusy(true);

    // ChatGPT-style streaming: open SSE to /ai/ask-stream, append an
    // empty AI message immediately, then mutate its content as each
    // token arrives. The user watches the companion "type" their
    // reply in real-time. Same wall time as /ai/ask (~3-6s on Haiku)
    // but the perceived wait drops to ~1.5s before the first word.
    const base = api.defaults?.baseURL || "";
    const url = `${base.replace(/\/$/, "")}/ai/ask-stream`;
    let aiIdx = -1;
    try {
      // Pre-insert the AI message with empty content so the typing
      // cursor renders immediately. We track its index so subsequent
      // token events mutate the right bubble.
      setMessages((m) => {
        aiIdx = m.length;
        return [...m, { role: "ai", content: "", streaming: true, ...(meta || {}) }];
      });

      const ctrl = new AbortController();
      const timeoutId = setTimeout(() => ctrl.abort(), 90000);
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(localStorage.getItem("hw_auth_token")
            ? { Authorization: `Bearer ${localStorage.getItem("hw_auth_token")}` }
            : {}),
        },
        body: JSON.stringify({ question: text, session_id: sessionId }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) throw new Error(`stream HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullAnswer = "";

      const apply = (eventName, data) => {
        if (eventName === "session" && data?.session_id && !sessionId) {
          setSessionId(data.session_id);
        } else if (eventName === "token") {
          fullAnswer += data.text;
          setMessages((m) => {
            const out = [...m];
            // aiIdx may have shifted if the user sent another message
            // mid-stream — find by streaming flag as a fallback.
            const idx = out[aiIdx]?.streaming
              ? aiIdx
              : out.findIndex((x) => x.streaming);
            if (idx >= 0) {
              out[idx] = { ...out[idx], content: out[idx].content + data.text };
            }
            return out;
          });
        } else if (eventName === "done") {
          setMessages((m) => {
            const out = [...m];
            const idx = out[aiIdx]?.streaming
              ? aiIdx
              : out.findIndex((x) => x.streaming);
            if (idx >= 0) {
              const finalContent = out[idx].content || "I couldn't find an answer just now. Try rephrasing?";
              out[idx] = { ...out[idx], content: finalContent, streaming: false };
            }
            return out;
          });
        }
      };

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const block = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = block.split("\n");
          let evName = "message";
          let dataStr = "";
          for (const ln of lines) {
            if (ln.startsWith("event:")) evName = ln.slice(6).trim();
            else if (ln.startsWith("data:")) dataStr += ln.slice(5).trim();
          }
          if (dataStr) {
            try { apply(evName, JSON.parse(dataStr)); } catch (_) {}
          }
        }
      }
      clearTimeout(timeoutId);

      if (typeof onComplete === "function") {
        try { onComplete(fullAnswer); } catch (_) { /* noop */ }
      } else if (narrate) {
        setTimeout(() => speakAnswer(fullAnswer), 250);
      }
    } catch (err) {
      setMessages((m) => {
        const out = [...m];
        const idx = out.findIndex((x) => x.streaming);
        if (idx >= 0) {
          out[idx] = { role: "ai", content: "Something went wrong. Please try again in a moment.", error: true };
        } else {
          out.push({ role: "ai", content: "Something went wrong. Please try again in a moment.", error: true });
        }
        return out;
      });
    } finally {
      setBusy(false);
    }
  };

  const savePrayer = async (idx) => {
    const m = messages[idx];
    if (!m || m.saved) return;
    try {
      await api.post("/prayers-journal", {
        text: m.content,
        title: m.kind === "prayer" ? "Pray with me" : null,
        passage: m.passage || null,
        language: m.language || language || "en",
      });
      setMessages((arr) => arr.map((x, i) => i === idx ? { ...x, saved: true } : x));
      toast.success("Saved to My Prayers");
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sign in to save prayers");
      } else {
        toast.error("Couldn't save prayer");
      }
    }
  };

  const prayWithMe = async () => {
    if (busy) return;
    const langCfg = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];
    // Try server's record first; fall back to localStorage; fall back to a generic prayer.
    let book = null;
    let chapter = null;
    let bookName = null;
    try {
      const { data } = await api.get("/reading/last");
      if (data && data.book && data.chapter) {
        book = data.book;
        chapter = data.chapter;
        bookName = data.book_name || data.book;
      }
    } catch (_) { /* not signed in — fall through */ }
    if (!book) {
      try {
        const cached = JSON.parse(localStorage.getItem("lumen:last-read") || "null");
        if (cached && cached.book && cached.chapter) {
          book = cached.book;
          chapter = cached.chapter;
          bookName = cached.book_name || cached.book;
        }
      } catch (_) { /* ignore */ }
    }
    const passage = book && chapter ? `${bookName || book} ${chapter}` : null;
    const displayText = passage
      ? `Pray with me — lead me through ${passage} in ${langCfg.label}.`
      : `Pray with me — a 1-minute guided prayer in ${langCfg.label}.`;
    const prompt = passage
      ? (
          `Lead me in a one-minute guided prayer rooted in ${passage}, the chapter I am currently reading. ` +
          `Speak directly to me. Weave the chapter's themes and at least one KJV verse from that chapter into the prayer. ` +
          `Open with an invocation, then 3–4 short petitions, then a quiet closing "Amen." ` +
          `Write the entire prayer in ${langCfg.label}. Do not add commentary before or after — only the prayer itself.`
        )
      : (
          `Lead me in a one-minute guided prayer for this moment. ` +
          `Open with an invocation, then 3–4 short petitions for peace, courage, gratitude, and surrender, then a quiet closing "Amen." ` +
          `Quote one short KJV verse inside the prayer. Write the entire prayer in ${langCfg.label}. ` +
          `Do not add commentary before or after — only the prayer itself.`
        );

    const speakPrayer = (answer) => {
      // Prayer narration uses the same shared narrator as the Companion.
      speakAnswer(answer, langCfg.code);
    };

    await send(null, prompt, displayText, speakPrayer, {
      kind: "prayer",
      passage: passage || null,
      language: langCfg.code,
    });
  };

  const [videoCtx, setVideoCtx] = useState(null); // {title, author} of the video being watched
  const [pendingQ, setPendingQ] = useState(null); // question typed into the Ask-AI bar

  const suggestions = videoCtx ? [
    `What scripture goes with "${videoCtx.title}"?`,
    `Give me a short devotional inspired by "${videoCtx.title}"`,
    `Write a prayer based on ${videoCtx.author ? `${videoCtx.author}'s` : "this"} video "${videoCtx.title}"`,
    "What does the Bible say about forgiveness?",
  ] : [
    "What does the Bible say about forgiveness?",
    "Explain John 3:16 in simple terms",
    "How do I pray when I'm anxious?",
    "Who was King David?",
  ];

  // Inline triggers (header + video action rows) open the AI panel.
  // The under-video sparkle passes the current video as context so the
  // Companion opens already knowing what the user is watching.
  useEffect(() => {
    const onOpen = (e) => {
      setVideoCtx(e?.detail?.context?.title ? e.detail.context : null);
      if (e?.detail?.question) setPendingQ(e.detail.question);
      setOpen(true);
    };
    window.addEventListener("holywords:open-ai", onOpen);
    return () => window.removeEventListener("holywords:open-ai", onOpen);
  }, []);

  // Auto-send a question handed over by the Ask-AI search bar.
  useEffect(() => {
    if (!open || !pendingQ || busy) return;
    const q = pendingQ;
    setPendingQ(null);
    send(null, q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingQ, busy]);

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-[70] bg-ink/40 backdrop-blur-sm grid place-items-end md:place-items-center p-0 md:p-6 animate-fade-up" onClick={() => setOpen(false)} data-testid="ai-overlay">
          <div
            className="bg-cream-100 w-full md:max-w-lg md:w-[480px] h-[85vh] md:h-[640px] rounded-t-3xl md:rounded-3xl shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="ai-panel"
          >
            <div className="bg-gradient-to-br from-forest to-forest-600 text-cream p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-cream/15 grid place-items-center">
                <Robot size={20} weight="duotone" className="text-[#E7C47C]" />
              </div>
              <div className="flex-1">
                <p className="font-heading text-lg leading-none">Holy Words Companion</p>
                <p className="text-[10px] tracking-[0.2em] uppercase text-cream/70 mt-1">AI · Scripture-grounded</p>
              </div>
              <button
                onClick={() => {
                  if (narrate) { try { stopAudio(); } catch (_) {} }
                  setNarrate((v) => !v);
                }}
                data-testid="ai-narrate-toggle"
                title={narrate ? "Mute Companion voice" : "Read replies aloud"}
                aria-label={narrate ? "Mute Companion voice" : "Read replies aloud"}
                className={`w-9 h-9 grid place-items-center rounded-full transition-all ${narrate ? "bg-cream/20 text-[#E7C47C]" : "hover:bg-cream/15 text-cream/70"}`}
              >
                {narrate ? <SpeakerHigh size={16} weight="fill" /> : <SpeakerSlash size={16} weight="fill" />}
              </button>
              <button onClick={() => setOpen(false)} data-testid="ai-close" className="w-9 h-9 grid place-items-center rounded-full hover:bg-cream/15 transition-all">
                <X size={18} weight="bold" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className="flex flex-col items-start max-w-[88%] gap-1">
                    <div
                      data-testid={`ai-message-${m.role}-${i}`}
                      className={`px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                        m.role === "user"
                          ? "bg-terracotta text-cream rounded-br-md self-end"
                          : m.error
                          ? "bg-red-50 text-red-700 border border-red-200 rounded-bl-md"
                          : "bg-white border border-cream-300 text-ink rounded-bl-md"
                      }`}
                    >
                      {m.content}
                      {m.streaming && (
                        <span className="inline-block w-0.5 h-4 bg-forest ml-0.5 align-middle animate-pulse" />
                      )}
                    </div>
                    {m.role === "ai" && !m.error && !m.streaming && i > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <button
                          onClick={() => speakAnswer(m.content, m.language)}
                          data-testid={`ai-replay-${i}`}
                          className="text-[11px] inline-flex items-center gap-1.5 px-3 h-7 text-ink/70 hover:bg-forest hover:text-cream transition-all"
                          aria-label="Read this reply aloud"
                          title="Read aloud"
                        >
                          <SpeakerHigh size={12} weight="fill" />
                          Read aloud
                        </button>
                        {m.kind === "prayer" && (
                          <button
                            onClick={() => savePrayer(i)}
                            disabled={m.saved}
                            data-testid={`ai-save-prayer-${i}`}
                            className={`text-[11px] inline-flex items-center gap-1.5 px-3 h-7 rounded-full transition-all ${
                              m.saved
                                ? "bg-terracotta/15 text-terracotta cursor-default"
                                : "bg-cream-200 text-ink hover:bg-terracotta hover:text-cream"
                            }`}
                            aria-label="Save this prayer"
                          >
                            <Heart size={12} weight={m.saved ? "fill" : "bold"} />
                            {m.saved ? "Saved to My Prayers" : "Save this prayer"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start" data-testid="ai-typing">
                  <div className="px-4 py-3 rounded-2xl bg-white border border-cream-300 flex gap-1 items-center">
                    <span className="w-1.5 h-1.5 bg-forest rounded-full animate-pulse" />
                    <span className="w-1.5 h-1.5 bg-forest rounded-full animate-pulse" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-forest rounded-full animate-pulse" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-2 pt-1 space-y-2">
                {videoCtx && (
                  <p data-testid="ai-video-context" className="text-[11px] text-ink/50 px-1 truncate">
                    Watching: <span className="font-semibold text-ink/70">{videoCtx.title}</span>{videoCtx.author ? ` · ${videoCtx.author}` : ""}
                  </p>
                )}
                <button
                  onClick={prayWithMe}
                  disabled={busy}
                  data-testid="ai-pray-with-me"
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-gradient-to-r from-terracotta/95 to-terracotta-600 text-cream hover:brightness-105 disabled:opacity-50 transition-all shadow-sm"
                >
                  <span className="w-9 h-9 rounded-full bg-cream/15 grid place-items-center shrink-0">
                    <HandsPraying size={18} weight="duotone" className="text-[#E7C47C]" />
                  </span>
                  <span className="flex-1 text-left">
                    <span className="block text-sm font-semibold leading-tight">Pray with me</span>
                    <span className="block text-[11px] opacity-90 leading-tight mt-0.5">A 1-minute prayer, read aloud in your language, rooted in what you just read.</span>
                  </span>
                </button>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      onClick={() => { setInput(s); }}
                      data-testid={`ai-suggestion-${s.slice(0, 10).replace(/\s/g, "-")}`}
                      className="text-[11px] px-3 h-8 text-ink/70 hover:text-ink transition-all"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={send} className="border-t border-cream-300 p-3 flex flex-col gap-2" data-testid="ai-form">
              {/* AI disclosure required by Google Play's
                  generative-AI policy (2025). Inline so reviewers
                  and users see it the moment they interact. */}
              <p className="text-[10px] text-muted_ink/70 leading-snug px-1">
                Responses are AI-generated and may contain mistakes — verify important spiritual decisions with Scripture or a trusted leader.
              </p>
              <div className="flex gap-2 items-end">
              <div className="relative flex-1">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Ask anything…"
                  rows={1}
                  disabled={busy}
                  data-testid="ai-input"
                  className="w-full resize-none px-4 py-3 pr-12 rounded-2xl bg-white border border-cream-300 outline-none focus:border-forest text-sm max-h-32"
                />
                <div className="absolute bottom-2 right-2">
                  <DictationButton size={36} testId="ai-dictation" label="Speak your question"
                    onTranscript={(t) => setInput((prev) => (prev || "") + t)}
                    onDone={(t) => {
                      const q = ((input || "") + " " + t).trim();
                      if (q) { setInput(""); send(null, q); }
                    }} />
                </div>
              </div>
              <button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="ai-send"
                className="w-11 h-11 text-forest hover:text-terracotta grid place-items-center hover:bg-forest-600 disabled:opacity-50 transition-all"
              >
                <PaperPlaneRight size={16} weight="fill" />
              </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
